/**
 * The ABA (Cemtex) direct entry file.
 *
 * This is what pay.com.au and Westpac Online Banking read to pay a batch of people in
 * one upload. It is a fixed width format: every record is exactly 120 characters and a
 * field in the wrong column is not a formatting problem, it is a rejected file, or a
 * payment to the wrong account.
 *
 * Three record types:
 *   0  descriptive  - who is sending the file, and when it should be processed
 *   1  detail       - one per payment
 *   7  file total   - the totals and the count, which the bank checks against the 1s
 *
 * Nothing here reads or writes the database. It takes numbers, returns a string, and
 * refuses when the numbers are wrong, so the refusal is testable without a bank.
 */

export interface AbaHeader {
  /** Three letter abbreviation of our own bank, e.g. WBC. */
  bankCode: string
  /** The name we bank under. 26 characters. */
  userName: string
  /** The six digit direct entry user ID (APCA number) the bank issued. */
  userId: string
  /** Up to 12 characters, shows on our own statement. */
  description: string
  /** ISO yyyy-mm-dd. The day the batch should be processed. */
  processingDate: string
  /** Our own BSB, for the trace record on every payment. */
  traceBsb: string
  /** Our own account number, for the trace record. */
  traceAccount: string
  /** Up to 16 characters. This is what the payee sees on their statement. */
  remitterName: string
}

export interface AbaPayment {
  bsb: string
  accountNumber: string
  /** The name the account is held in. 32 characters. */
  accountTitle: string
  amountCents: number
  /** Up to 18 characters. What the payee sees against the amount. */
  reference: string
}

export interface AbaProblem {
  /** 0 for the header, otherwise the 1 based position of the payment. */
  row: number
  field: string
  message: string
}

const digitsOnly = (v: string) => String(v || '').replace(/[^0-9]/g, '')

/** '032002' or '032-002' both arrive; the file wants '032-002'. */
export function normaliseBsb(value: string): string {
  const d = digitsOnly(value)
  return d.length === 6 ? d.slice(0, 3) + '-' + d.slice(3) : ''
}

/** Left justified, blank filled, truncated. */
const text = (value: string, width: number) =>
  String(value || '')
    .replace(/[^\x20-\x7E]/g, ' ')
    .slice(0, width)
    .padEnd(width, ' ')

/** Right justified, zero filled. */
const zeros = (value: number, width: number) =>
  String(Math.round(Math.abs(value))).padStart(width, '0').slice(-width)

/** Right justified, blank filled. Account numbers use this, not zero fill. */
const rightBlank = (value: string, width: number) =>
  String(value || '')
    .slice(0, width)
    .padStart(width, ' ')

const blanks = (width: number) => ' '.repeat(width)

function ddmmyy(iso: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(iso || ''))
  if (!m) return ''
  return m[3] + m[2] + m[1].slice(2)
}

/**
 * Everything wrong with the batch, in one pass, so the person fixes it once instead of
 * discovering the next problem after each save.
 */
export function validateAba(header: AbaHeader, payments: AbaPayment[]): AbaProblem[] {
  const problems: AbaProblem[] = []
  const H = (field: string, message: string) => problems.push({ row: 0, field, message })

  if (!/^[A-Za-z]{3}$/.test(String(header.bankCode || '').trim())) {
    H('bankCode', 'Needs the three letter bank abbreviation, e.g. WBC.')
  }
  if (!String(header.userName || '').trim()) {
    H('userName', 'Needs the name the account is banked under.')
  }
  if (digitsOnly(header.userId).length !== 6) {
    H('userId', 'The direct entry user ID is six digits. Your bank issued it.')
  }
  if (!normaliseBsb(header.traceBsb)) {
    H('traceBsb', 'Our own BSB is missing or is not six digits.')
  }
  if (!digitsOnly(header.traceAccount)) {
    H('traceAccount', 'Our own account number is missing.')
  }
  if (!String(header.remitterName || '').trim()) {
    H('remitterName', 'Needs the name the payee will see on their statement.')
  }
  if (!ddmmyy(header.processingDate)) {
    H('processingDate', 'Needs a processing date.')
  }

  if (!payments.length) {
    H('payments', 'Nothing selected to pay.')
  }

  payments.forEach((p, i) => {
    const row = i + 1
    const who = p.accountTitle || 'this payment'
    if (!normaliseBsb(p.bsb)) {
      problems.push({ row, field: 'bsb', message: who + ': BSB is missing or is not six digits.' })
    }
    if (!digitsOnly(p.accountNumber)) {
      problems.push({ row, field: 'accountNumber', message: who + ': account number is missing.' })
    }
    if (digitsOnly(p.accountNumber).length > 9) {
      problems.push({ row, field: 'accountNumber', message: who + ': account number is longer than nine digits.' })
    }
    if (!String(p.accountTitle || '').trim()) {
      problems.push({ row, field: 'accountTitle', message: 'A payment has no account name.' })
    }
    if (!Number.isFinite(p.amountCents) || Math.round(p.amountCents) <= 0) {
      problems.push({ row, field: 'amountCents', message: who + ': amount must be more than zero.' })
    }
  })

  const total = payments.reduce((s, p) => s + Math.round(p.amountCents || 0), 0)
  if (total > 9999999999) {
    H('total', 'The batch total is larger than an ABA file can carry.')
  }

  return problems
}

/**
 * Builds the file. Throws if validateAba would have complained, because a half valid
 * ABA file is more dangerous than none.
 */
export function buildAbaFile(header: AbaHeader, payments: AbaPayment[]): string {
  const problems = validateAba(header, payments)
  if (problems.length) {
    throw new Error(problems.map((p) => p.message).join(' '))
  }

  const traceBsb = normaliseBsb(header.traceBsb)
  const traceAccount = digitsOnly(header.traceAccount)

  // Record 0 - descriptive
  const record0 =
    '0' +
    blanks(17) +
    '01' +
    text(header.bankCode.toUpperCase(), 3) +
    blanks(7) +
    text(header.userName, 26) +
    zeros(Number(digitsOnly(header.userId)), 6) +
    text(header.description, 12) +
    ddmmyy(header.processingDate) +
    blanks(40)

  // Record 1 - one per payment. Transaction code 50 is a general credit.
  const details = payments.map(
    (p) =>
      '1' +
      normaliseBsb(p.bsb) +
      rightBlank(digitsOnly(p.accountNumber), 9) +
      ' ' +
      '50' +
      zeros(p.amountCents, 10) +
      text(p.accountTitle, 32) +
      text(p.reference, 18) +
      traceBsb +
      rightBlank(traceAccount, 9) +
      text(header.remitterName, 16) +
      zeros(0, 8),
  )

  const total = payments.reduce((s, p) => s + Math.round(p.amountCents), 0)

  // Record 7 - file total. Net is credits minus debits; we only ever write credits, so
  // net and credit are the same number and debits are zero.
  const record7 =
    '7' +
    '999-999' +
    blanks(12) +
    zeros(total, 10) +
    zeros(total, 10) +
    zeros(0, 10) +
    blanks(24) +
    zeros(payments.length, 6) +
    blanks(40)

  const lines = [record0, ...details, record7]

  const wrong = lines.findIndex((l) => l.length !== 120)
  if (wrong >= 0) {
    // A defensive check on our own arithmetic. If this ever fires the file would have
    // been rejected by the bank anyway, and this says which record to look at.
    throw new Error('ABA record ' + wrong + ' came out ' + lines[wrong].length + ' characters, not 120.')
  }

  return lines.join('\r\n') + '\r\n'
}

/** DirectAuto-2026-09-01.aba */
export function abaFileName(processingDate: string): string {
  const day = String(processingDate || '').slice(0, 10) || 'undated'
  return 'DirectAuto-' + day + '.aba'
}
